import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import styles from './product-area-element.module.scss';
import { useServices } from '@/services-context';
import ProductGrid2 from '../product-grid-2';
import FiltersElementMain from '../product-area/filters-element-main';
import { useProductArea } from '../product-area/context';
import TagsElement from '../tags-element';
import SorterElement from '../sorter-element';
import FiltersButtonElement from '../filters-button-element';
import { SortersButtonElement } from '../sorters-button-element';
import FixBackButton from '../fix-back-button';
import ProductAreaFilterPopup from '../product-area-filter-popup';
import { ProductAreaSorterPopup } from '../product-area-sorter-popup';
import FixPagerGroup from '../fix-pager-group';
import FixText from '@/elements-react/fix-text';
import { LayoutStyleTypes } from "@/util-react/layout";
import { createProductUrl } from "@/util/category.ts";
import { JsonLdDataset } from "@/services/json-ld.ts";
import { PageTags } from "@/elements-react/document-elements/page-tags.tsx";
import { CurrencyConverter } from "@/util/currency-converter.ts";
import { Gtag } from "@/services/gtag.ts";
import { B2cProductPayload, B2cProductSearchPayload } from "@/generated/graphql/b2c.ts";
import { getCategoriesEcommerceItems } from "@/helpers/google-events.ts";
import { State } from "@/pages-react/catalog-page-common/page-store.tsx";
import { useMemo, useRef } from "preact/hooks";
import { RefObject } from "preact";
import {useLocation} from "preact-iso";

export type ProductAreaElementProps = {
  type: LayoutStyleTypes.TYPE_SM | LayoutStyleTypes.TYPE_LG;
  resultState: State;
  aboutText?: string;
  pageType: "category" | "brand" | "search";
  sendAction: RefObject<() => void | null>;
};

export const ProductAreaElement = ({ type, resultState, aboutText, pageType, sendAction }: ProductAreaElementProps) => {
  const { t } = useTranslation();
  const services = useServices();
  const location = useLocation();
  const store = useProductArea();
  const groupKey = 'main' as const;
  const group = store.groups[groupKey]!;
  const isSorterActive = store.popup === 'sorter';
  const openSorter = () => store.openSorterPopup();
  const openFilters = () => store.openFiltersPopup();
  const unitedSearchResult = services.customerChannel.isUnitedSearchResult && pageType === "search";

  const initialPage = useMemo(() => group.page, []);

  const jsonLdDataset: JsonLdDataset = useMemo(() => {
    if (!store.products || !store.products?.length) {
      return {};
    }

    let minPrice = 0;
    let maxPrice = 0;

    const productList = store.products.map((item) => {
      const productName = item.productInfo?.name ?? "";

      if (minPrice === 0 || (minPrice > item.minPrice && item.minPrice > 0)) {
        minPrice = item.minPrice;
      }

      if (maxPrice < item.maxPrice) {
        maxPrice = item.maxPrice;
      }

      return {
        url: createProductUrl(item, services.categories),
        name: `${productName} ${item.mpnFormatted}`,
      };
    });

    minPrice = services.formatter.getCustomerMoneyValue(CurrencyConverter.convertCents(minPrice), true).amount;
    maxPrice = services.formatter.getCustomerMoneyValue(CurrencyConverter.convertCents(maxPrice), true).amount;

    return {
      productList: {
        items: productList,
        count: store.totalItems,
        page: initialPage ?? 1,
      },
      aggregated: {
        lowPrice: parseFloat(minPrice.toString()).toFixed(2),
        highPrice: parseFloat(maxPrice.toString()).toFixed(2),
        priceCurrency: services.formatter.currencyCode.toUpperCase(),
        offerCount: productList.length,
      },
    };
  }, [store.products]);

  const renderFiltersPopup = () => (
    <ProductAreaFilterPopup />
  );
  const renderSorterPopup = () => (
    <ProductAreaSorterPopup />
  );

  const renderActivePopup = () => {
    switch (store.popup) {
      case 'filters':
        return renderFiltersPopup();
      case 'sorter':
        return renderSorterPopup();
      default:
        return null;
    }
  };

  const renderNoProducts = () => {
    const onBack = () => store.applyBackToSearchButton();
    return (
      <div className={styles.mainHasNoProducts}>
        <div className={styles.noProducts}>
          <div className={classNames(styles.text)}>{t('ct7yjv6q1dnhh9ng.not-found-in-category', 'Sorry, there are currently no products in this category')}</div>
          <div className={classNames(styles.subtext)}>{t('e96vlks4gy9wj8ex.check-back-later', 'Please check back later')}</div>
          <FixBackButton className={classNames(styles.back)} onClick={onBack} />
        </div>
      </div>
    );
  };

  const renderResultHeadingLg = () => (
    <div className={classNames(styles.top)}>
      <div className={classNames(styles.selections)}>
        <TagsElement />
      </div>
      <div className={classNames(styles.sorters)}>{!unitedSearchResult && <SorterElement />}</div>
    </div>
  );

  const itemsGoogleEvent = useRef<any[]>([]);
  useMemo(() => {
    itemsGoogleEvent.current.length = 0;
    const itemsGoogleEventTmp = resultState.loadedProducts.map((item: B2cProductSearchPayload, i: number) => {
      const getItemListName = () => {
        switch (pageType) {
          case "category":
            return `Category page ${resultState.category.name}`;
          case "brand":
            return `Brand page ${resultState.brand.name}`;
          case "search":
            return "Product search results page";
          default:
            return "";
        }
      };

      return {
        item_name: `${item.mpnFormatted} ${item.brandName} ${item.productInfo.name}`,
        item_id: item.id,
        price: CurrencyConverter.convertCents(item.minPrice),
        item_brand: item.brandName,
        item_list_name: getItemListName(),
        index: i,
        quantity: "1",
        ...getCategoriesEcommerceItems(item.categories, services),
      };
    });

    itemsGoogleEvent.current.push(...itemsGoogleEventTmp);
  }, [JSON.stringify(resultState.loadedProducts)]);

  sendAction.current = () => {
    Gtag.pushEventEcommerce("view_item_list", { items: itemsGoogleEvent.current });
  };

  const getExchangePrices = (exchange: B2cProductPayload) => {
    const price = exchange.prices?.[0]?.price.amount * 100 ?? 0;
    return [price, price];
  };

  const productFromData = (item: any) => {
    return {
      id: item.id,
      name: item.productInfo?.name ?? "",
      mpn: item.mpnFormatted,
      brand: item.brandName,
      price: item.prices ? getExchangePrices(item) : [item.minPrice, item.maxPrice],
      discount: 15,
      images: item.productAssets.filter((asset: any) => asset.type === "image"),
      url: createProductUrl(item, services.categories),
      originalMpn: item.originalMpn,
      exchanged: item.exchanged,
      exchanges:
        item.exchanges?.map((exchange: any) => productFromData({ ...exchange, originalMpn: item.mpnFormatted, brandName: exchange.brand?.name })) ?? [],
      crosses: item.analogues,
      state: item.state,
      stockType: item.stockType,
      isHidePriceIfProductIsOutOfStock: item.isHidePriceIfProductIsOutOfStock,
      deliveryStandardTimesIsNotDefined: item.deliveryStandardTimesIsNotDefined,
    };
  };

  const renderProducts = () => {
    const items = store.products.map((item: any) => productFromData(item));

    return (
      <div>
        {type === LayoutStyleTypes.TYPE_LG && renderResultHeadingLg()}
        <div className="grid">
          <ProductGrid2 data={items} googleEventData={itemsGoogleEvent.current} type={type} unitedSearchResult={unitedSearchResult} />
        </div>
        {!unitedSearchResult && renderPagination()}
      </div>
    );
  };

  const renderPagination = () => {

    return (
      <div className={styles.pagination}>
        <FixPagerGroup
          current={resultState.currentPage?? 0}
          total={resultState.totalPages}
          totalItems={resultState.totalItems}
          currentUrl={location.url}
          size={20}
          pager={true}
          type={type}
          onChange={store.applyPage}
        />
      </div>
    );
  };

  const renderResults = () => {
    switch (resultState.resultState) {
      case 'content':
        return renderProducts();
      case 'empty':
        return renderNoProducts();
      default:
        return null;
    }
  };

  const renderAboutCard = () => (
    <div className={classNames(styles.section, styles.card, styles['type-1'])}>
      <div className={classNames(styles.sectionTitle, 'section-title')}>{t('9us00h798g54t0wt.category-description', 'Category description')}</div>
      <div className={styles.sectionBox}>
        <FixText type={type} text={aboutText!} />
      </div>
    </div>
  );

  const renderContentSm = () => (
    <>
      <div className={classNames(styles.section, 'card', styles[LayoutStyleTypes.TYPE_SM])}>
        <div className={classNames(styles.sectionTitle, 'section-title')}>
          {t('o77l25315i2v5mvs.featured-products', 'Featured products')}
        </div>
        <div className={classNames(styles.sorters, styles.sectionControls, 'sorters')}>
          {!unitedSearchResult && <FiltersButtonElement onClick={openFilters} />}
          {!unitedSearchResult && <SortersButtonElement active={isSorterActive} onClick={openSorter} />}
        </div>
        <div className="section-box">{renderResults()}</div>
      </div>
      {/* TODO: Is it necessary? {renderAboutCard()}*/}
      {renderActivePopup()}
    </>
  );

  const renderContentLg = () => (
    <div className={classNames(styles.card, 'card')}>
      <div className={styles.sidebar}>
        {!unitedSearchResult &&  <FiltersElementMain type={LayoutStyleTypes.TYPE_LG} group={groupKey} />}
      </div>
      <div className={styles.main}>
        {renderResults()}
        {/* TODO: Is it necessary? {renderAboutCard()}*/}
      </div>
    </div>
  );

  const content = type === LayoutStyleTypes.TYPE_SM ? renderContentSm() : renderContentLg();

  return (
    <div style={{ display: "contents" }}>
      {content}
      <PageTags jsonld={jsonLdDataset} />
    </div>
  );
};

export default ProductAreaElement;
